/**
 * 跟进记录填写模块
 * @author wzwwu
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, message, Modal, Popover, Input, Select, Alert } from 'antd'
import { addSdrFollowRecord, getFollowFormItemList } from '@services/customer'
import FormItemRender from '../../components/FormItemRender'
import {
  ExtendItem,
  FollowStatusEnum,
  FormItemType,
  IFollowFormItem,
  SdrFollowRecordPayload,
} from '@types'
import { cache, createForm, Form, formatTime, notEmptyArray, sStorage } from '@library'
import { customerDetailStore } from '@pages/customer/detail/store'
import { useShallow } from 'zustand/react/shallow'
import { useCustomerActionsContext } from '@pages/customer/detail/hooks/CustomerActionsContext'
import { CommonSelector, CommonCascador } from '@components'
import { msgLogs, phoneLogs } from '../../../../../components/CommonSelector/configs'
import { sessionStore } from '@store'
import moment from 'moment'
import type { RangePickerProps } from 'antd/es/date-picker'
import { isEmpty } from 'lodash'
import { CommunicateTypeOptions } from '@configs/constants'
import { customerIntention, industryProductsOnlyValid } from 'src/components/CommonCascador/configs'

const formStore = createForm()
const { useValues, useValidate, Field, withForm } = formStore

const { confirm } = Modal

enum SubmitTypeEnum {
  SUBMIT_ONLY = 1,
  SUBMIT_AND_NEXT = 4,
  SUBMIT_AND_DISCARD = 2
}

const FollowStatusMap = new Map([
  [SubmitTypeEnum.SUBMIT_ONLY, FollowStatusEnum.SAVE],
  [SubmitTypeEnum.SUBMIT_AND_NEXT, FollowStatusEnum.SAVE],
  [SubmitTypeEnum.SUBMIT_AND_DISCARD, FollowStatusEnum.DISCARD],
])

const confirmMessageMap = new Map([
  [SubmitTypeEnum.SUBMIT_ONLY, '保存'],
  [SubmitTypeEnum.SUBMIT_AND_NEXT, '保存&下一个'],
  [SubmitTypeEnum.SUBMIT_AND_DISCARD, '保存并弃单'],
])

export const CommunicateResultOptions = [
  {
    label: '未拨打',
    value: 5,
  },
  {
    label: '未接通',
    value: 6,
  },
  {
    label: '秒挂',
    value: 1,
  },
  {
    label: '30s',
    value: 2,
  },
  {
    label: '30~89s',
    value: 3,
  },
  {
    label: '90s及以上',
    value: 4,
  },
]

export const getValueByType = (item: IFollowFormItem, value: any) => {
  if (item?.type === FormItemType.DATE) return formatTime(value) || ''
  if (item?.type === FormItemType.MUTIL_SELECT) {
    return value || []
  }
  return value || ''
}

function AddFollowRecordPanelSdr ({ visible, onCancel }) {
  const userInfo = sessionStore(s => s.userInfo)

  const { validate } = useValidate()
  const { values, resetValues, setInitialValues } = useValues()

  const { customerId, cid, customerInfo, contactInfoList, selectedTaskIds, showNext, gotoNextCustomer } = customerDetailStore(useShallow(s => ({
    customerId: s.customerId,
    cid: s.cid,
    customerInfo: s.customerInfo,
    contactInfoList: s.contactInfoList,
    selectedTaskIds: s.selectedTaskIds,
    showNext: s.showNext,
    gotoNextCustomer: s.gotoNextCustomer,
  })))
  const { msgLogIds, phoneLogIds, setPhoneLogIds, setMsgLogIds } = useCustomerActionsContext()

  // 设置初始值
  useEffect(() => setInitialValues({ followType: ['phone'] }), [])
  /* 获取表单渲染的JSON数据 */
  const [formItemList, setFormItemList] = useState<IFollowFormItem[]>([])
  useEffect(() => {
    (async () => {
      const [res, err] = await getFollowFormItemList()
      if (err) {
        message.error(`获取渲染表单数据失败，请刷新页面重试(${err.message})`)
      } else {
        const list = res?.list
        setFormItemList(list)
      }
    })()
  }, [])

  /* 刷新或关闭页面时候,自动保存和恢复短信和电话记录 */
  const restoreKey = useMemo(() => 'AddFollowRecordPanelSdr-' + customerId, [customerId])
  useEffect(() => {
    const { phoneLogIds, msgLogIds } = sStorage.get<{ phoneLogIds: string[], msgLogIds: string[] }>(restoreKey) || {}
    if (notEmptyArray(phoneLogIds) || notEmptyArray(msgLogIds)) {
      setPhoneLogIds(phoneLogIds)
      setMsgLogIds(msgLogIds)
      message.info('已自动恢复未提交的通话和短信记录')
    }
    sStorage.remove(restoreKey)
  }, [restoreKey, setPhoneLogIds, setMsgLogIds])
  useEffect(() => {
    if (notEmptyArray(phoneLogIds) || notEmptyArray(msgLogIds)) {
      sStorage.set(restoreKey, { phoneLogIds, msgLogIds })
    }
  }, [phoneLogIds, msgLogIds, restoreKey])

  /* 短息和通话记录 - 支持手动关联更多通话记录 */
  const [isLinkMore, setIsLinkMore] = useState(false)
  /* 是否已显示过通话记录警告 */
  const [hasShownPhoneLogWarning, setHasShownPhoneLogWarning] = useState(false)

  /* 当用户关联了通话记录后，重置警告状态 */
  useEffect(() => {
    if (!isEmpty(phoneLogIds) && hasShownPhoneLogWarning) {
      setHasShownPhoneLogWarning(false)
    }
  }, [phoneLogIds, hasShownPhoneLogWarning])

  // 提交跟进记录
  const handleSubmit = useCallback(async (followStatus: SubmitTypeEnum) => {
    const validateRes = await validate()
    if (!validateRes.isPass) return

    // 校验是否关联通话记录
    const isEmptyPhoneLog = isEmpty(phoneLogIds)
    if (isEmptyPhoneLog && !hasShownPhoneLogWarning) {
      // 第一次未关联通话记录时，展开关联区域并显示警告
      setIsLinkMore(true)
      setHasShownPhoneLogWarning(true)
      message.warning('当前未关联通话记录，请确认后再次点击保存')
      return
    }

    confirm({
      title: `确认进行${confirmMessageMap.get(followStatus)}操作吗？`,
      async onOk () {
        const extend: ExtendItem[] = []
        for (const [k, v] of Object.entries(values)) {
          const item = formItemList.find(item => item.key === k)
          const keyValue = getValueByType(item, v)
          if (item) {
            extend.push({
              key: k,
              keyValue,
              keyName: item?.keyName,
            })
          }
        }

        // 额外信息 这里的逻辑是 index页面会将其产生的短信和通话操作，这个操作后端会返回phoneLogIds和msgLogIds的数组。需要在填写跟进记录表单的时候顺带上送上去
        if (phoneLogIds?.length) {
          const keyValue = userInfo.useT3C ? `${phoneLogIds.map(item => `t3c-${item}`)}` : `${phoneLogIds}`
          extend.push({
            key: 'phoneLogIds',
            keyName: '通话记录',
            keyValue,
          })
        }
        if (msgLogIds?.length) {
          extend.push({
            key: 'msgLogIds',
            keyName: '短信记录',
            keyValue: `${msgLogIds}`,
          })
        }

        // 提交数据
        const payload: SdrFollowRecordPayload = {
          cid,
          taskIds: selectedTaskIds,
          extend: JSON.stringify(extend),
          followStatus: FollowStatusMap.get(followStatus),
          touchCustomer: values?.touchCustomer,
          followType: values?.followType,
          communicateResult: values?.communicateResult,
          intentionId: values?.intentionId?.[0],
          subIntentionId: values?.intentionId?.[1],
          remark: values?.remark,
          interestSmallProduct: values?.interestProducts?.[0],
          interestSubProduct: values?.interestProducts?.[1],
        }

        const [, err] = await addSdrFollowRecord(payload)
        if (err) {
          message.error(`保存失败(${err.message})`)
          return
        }
        resetValues({})
        sStorage.remove(restoreKey)
        cache.remove(`getHistoryFollowList-${JSON.stringify(customerId)}`)

        message.success('保存成功')

        if (followStatus === SubmitTypeEnum.SUBMIT_AND_NEXT) {
          await gotoNextCustomer()
        } else {
          setTimeout(() => window.location.reload(), 1000)
        }
      },
      onCancel () {},
    })
  }, [selectedTaskIds, values, phoneLogIds, msgLogIds, restoreKey, cid, hasShownPhoneLogWarning])

  const actionText = useMemo(() => {
    const isEmptyPhoneLog = isEmpty(phoneLogIds)
    const isEmptyMsgLog = isEmpty(msgLogIds)
    return (
      <>
        <span>已选择 <Popover title="已选择任务id" content={selectedTaskIds?.join(';')}>(<a>{selectedTaskIds?.length ?? 0}</a>)</Popover> 条任务。</span>
        <span>已关联({isEmptyPhoneLog ? 0 : phoneLogIds.length})条通话记录、</span>
        <span>({isEmptyMsgLog ? 0 : msgLogIds.length})条短信记录。</span>
        {!isLinkMore && <a onClick={() => setIsLinkMore(true)}>关联更多</a>}
        {isEmptyPhoneLog && hasShownPhoneLogWarning && (
          <Alert
            type="warning"
            message="当前未关联通话记录，请确认是否保存跟进记录"
            style={{ marginTop: 8 }}
            showIcon
          />
        )}
      </>
    )
  }, [phoneLogIds, msgLogIds, selectedTaskIds, customerInfo, isLinkMore, hasShownPhoneLogWarning])

  const range = (start: number, end: number) => {
    const result = []
    for (let i = start; i < end; i++) {
      result.push(i)
    }
    return result
  }

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && !(current > moment().subtract(1, 'day') && current < moment().add(2, 'years'))
  }

  const disabledDateTime = (current) => {
    if (!moment(current).isAfter(moment(), 'day')) {
      return {
        disabledHours: () => range(0, 24).splice(0, moment().hour() + 1),
      }
    }
    return {}
  }
  const phoneLogsConfig = phoneLogs(userInfo.useT3C)
  return (
    <Modal
      open={visible}
      wrapClassName="add-follow-record-panel"
      style={{ position: 'absolute', right: 22, top: 'calc(100vh - 694px)', height: 694 }}
      bodyStyle={{ height: '600px', overflowY: 'auto' }}
      getContainer={() => document.body}
      mask={false}
      width={650}
      title="填写跟进记录"
      maskClosable={false}
      onCancel={() => {
        setHasShownPhoneLogWarning(false)
        setIsLinkMore(false)
        onCancel()
      }}
      footer={
        <>
          <Button className="ml30" type="primary" onClick={() => handleSubmit(SubmitTypeEnum.SUBMIT_ONLY)}>保存</Button>
          {showNext && <Button className="ml30" onClick={() => handleSubmit(SubmitTypeEnum.SUBMIT_AND_NEXT)}>保存&下一个</Button>}
        </>
      }
    >
      <Form labelCol={{ span: 5 }} wrapperCol={{ span: 18 }}>
        {/* 本次跟进任务 为固定item，不通过json数据渲染 */}
        <Field
          field="taskIds"
          label="本次跟进任务"
          rule={{
            validator: () => {
              if (!selectedTaskIds?.length) return new Error('请选择任务')
              return true
            },
          }}
        >
          <>
            {actionText}
          </>
        </Field>
        {isLinkMore && (
          <>
            <Field field="linkedMorePhoneLogs" label="关联通话记录">
              {() => (
                <CommonSelector
                  {...phoneLogsConfig}
                  mode="multiple"
                  value={phoneLogIds}
                  onChange={logs => {
                    setPhoneLogIds(logs as string[])
                  }}
                  filter={{
                    cid: [cid],
                    salesName: userInfo.salesName,
                    createTimeFrom: formatTime(moment(), 'start'),
                    createTimeTo: formatTime(moment(), 'end'),
                  }}
                  placeholder="关联该客户下的今日通话记录"
                />
              )}
            </Field>
            <Field field="linkedMoreMsgLogs" label="关联短信记录">
              {() => (
                <CommonSelector
                  {...msgLogs}
                  value={msgLogIds}
                  onChange={logs => setMsgLogIds(logs as string[])}
                  mode="multiple"
                  filter={{
                    cid: [cid],
                    operator: userInfo.salesName,
                    sendBeginTime: formatTime(moment(), 'start'),
                    sendEndTime: formatTime(moment(), 'end'),
                  }}
                  placeholder="关联该客户下的今日短信记录"
                />
              )}
            </Field>
          </>
        )}
        <Field field="touchCustomer" label="本次联系人" required>
          <Select allowClear>
            {contactInfoList?.map(opt => (
              <Select.Option key={opt?.contactId} value={opt?.contactId}>{opt?.name}</Select.Option>
            ))}
          </Select>
        </Field>
        <Field field="followType" label="本次沟通方式" required>
          <Select mode="multiple" allowClear>
            {CommunicateTypeOptions.map(opt => (
              <Select.Option key={opt.key} value={opt.value}>{opt.name}</Select.Option>
            ))}
          </Select>
        </Field>
        <Field field="communicateResult" label="沟通是否有效" required>
          <Select allowClear>
            {CommunicateResultOptions.map(opt => (
              <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
            ))}
          </Select>
        </Field>
        <Field field="intentionId" label="客户最新意向" required>
          <CommonCascador {...customerIntention} />
        </Field>
        <Field field="remark" label="本次跟进内容" required>
          <Input.TextArea autoSize={{ minRows: 2 }} placeholder="请输入本次跟进内容" />
        </Field>
        <Field field="interestProducts" label="感兴趣的产品">
          <CommonCascador {...industryProductsOnlyValid} changeOnSelect />
        </Field>
        <FormItemRender formItemList={formItemList} formStore={formStore} disabledDate={disabledDate} disabledDateTime={disabledDateTime} />
      </Form>
    </Modal>
  )
}

export default withForm(AddFollowRecordPanelSdr)
